import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/faq")({
  head: () =>
    seoHead({
      path: "/faq",
      title: "FAQ — Coffman Cove Lodging",
      description:
        "Answers to common questions about lodging, boat and vehicle rentals, travel, fish processing and trip planning in Coffman Cove, Alaska.",
    }),
  component: FAQ,
});

const FAQS: { q: string; a: string }[] = [
  {
    q: "How do I get to Coffman Cove?",
    a: "Fly into Ketchikan (KTN), then either the Inter-Island Ferry to Hollis or a floatplane into Klawock. From either landing, our island transportation ($175–$200 per vehicle, one-way) will get you to the lodge. Book early — seats and floatplanes fill in peak season.",
  },
  {
    q: "What's included with lodging?",
    a: "Room, private bath, Wi-Fi, laundry access, homestyle breakfast and dinner, and a sack lunch for the boat. Not included: fishing licenses, fuel for boat rentals, fish processing/boxes, and gratuities.",
  },
  {
    q: "Do I need a fishing license?",
    a: "Yes — every angler needs a current Alaska sport fishing license, plus a King Salmon stamp if you're keeping kings. Purchase online at adfg.alaska.gov before you arrive, or at the general store in town.",
  },
  {
    q: "Are the boats self-run, or is a captain provided?",
    a: "All of our rentals are strictly self-guided — we do not provide captains, guides, or charter service. Our 20'/22' welded aluminum skiffs and Hewescraft boats are self-run. We include a safety briefing, GPS/sonar, VHF, survival suits, bait, ice and a starter tank of gas. Fuel above the starter tank is billed at cost.",
  },
  {
    q: "How does fish processing work?",
    a: "Hedges Marine Processing handles filleting, vacuum sealing and freezing on site (approx. $3/lb, ~$150 per box). Waxed airline-legal fish boxes are $15. Our walk-in freezer holds everything until you fly out.",
  },
  {
    q: "How much fish can I bring home?",
    a: "Airlines typically allow 50 lbs of frozen fish per checked box. Most guests fly home with 1–3 boxes each. We'll help you plan the timing so everything stays frozen door-to-door.",
  },
  {
    q: "What should I pack?",
    a: "Layers, waterproof rain jacket and pants, warm hat and gloves, polarized sunglasses, sturdy non-marking boat shoes, and a small dry bag. Rods, reels, tackle and bait are provided with boat rentals, but you're welcome to bring favorites.",
  },
  {
    q: "Is there cell service and Wi-Fi?",
    a: "Wi-Fi is available at the lodge. Cell coverage in Coffman Cove is limited — treat it as a real unplug. VHF handles boat-to-boat and boat-to-shore comms.",
  },
  {
    q: "What about medical emergencies?",
    a: "Coffman Cove is remote. We strongly recommend medivac coverage through Guardian Flight or LifeMed Alaska — both service Prince of Wales Island. Standard travel insurance often does not cover Alaska medivac.",
  },
  {
    q: "What's your cancellation policy?",
    a: "Deposits hold your dates. Reach out via the contact page for specifics — we work with guests case-by-case for weather, travel disruptions and emergencies.",
  },
];

function FAQ() {
  return (
    <div>
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-20 pb-16">
          <p className="uppercase tracking-[0.3em] text-xs text-white/70">Guest FAQ</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 max-w-3xl text-balance text-white">
            Answers before you ask.
          </h1>
          <p className="mt-5 max-w-2xl text-white/90">
            Everything travelers to Coffman Cove ask us most — travel, lodging, boats, licenses,
            processing and getting your fish home.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-6 py-16">
        <div className="divide-y divide-border rounded-lg border border-border bg-card">
          {FAQS.map((item) => (
            <details key={item.q} className="group p-6 open:bg-[var(--color-parchment)]/40">
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4">
                <h2 className="font-display text-lg md:text-xl">{item.q}</h2>
                <span className="mt-1 text-[var(--color-ember)] text-2xl leading-none transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                {item.a}
              </p>
            </details>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-muted-foreground">Still have a question?</p>
          <Link
            to="/contact"
            className="mt-3 inline-flex items-center rounded-md bg-[var(--color-deep)] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-[var(--color-ember)]"
          >
            Contact us →
          </Link>
        </div>
      </section>
    </div>
  );
}
